<script setup>
defineOptions({ name: "SolutionKaliFoodPage" });
import { defineAsyncComponent, onMounted, onUnmounted, ref } from "vue";
import {
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  DeviceTabletIcon,
  DocumentCheckIcon,
  CreditCardIcon,
  BanknotesIcon,
  ArchiveBoxIcon,
  PuzzlePieceIcon,
  ClipboardDocumentListIcon,
  QrCodeIcon,
  ScaleIcon as ScaleIconOutline,
} from "@heroicons/vue/24/outline";
import {
  BuildingStorefrontIcon,
  BoltIcon,
  FireIcon,
  CakeIcon,
  BeakerIcon,
  GiftTopIcon,
  TruckIcon,
  HomeModernIcon,
  ScaleIcon,
  ClockIcon,
  GiftIcon,
  ShoppingBagIcon,
} from "@heroicons/vue/24/solid";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SolutionHero from "@/components/ui/SolutionHero.vue";
import imageBg from "@/assets/solutions/kalifood.webp";
import visaoGeralImg from "@/assets/visaogeralfood.webp";
import gestaoTributaria from "@/assets/gestao-tributaria.webp";

const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));
const Clients = defineAsyncComponent(() => import("@/components/home/Clients.vue"));

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

const navLinks = [
  { label: "Visão geral", href: "#visao-geral" },
  { label: "Segmentos", href: "#segmentos" },
  { label: "Funcionalidades", href: "#funcionalidades" },
  { label: "Fiscal", href: "#fiscal" },
  { label: "Módulos", href: "#modulos" },
  { label: "Depoimentos", href: "#depoimentos" },
  { label: "Dúvidas", href: "#duvidas" },
];

const activeNav = ref(navLinks[0]?.href ?? "#visao-geral");
const setActiveNav = (href) => {
  activeNav.value = href;
};

const topOffsetPx = 140;
let scrollRaf = null;
const updateActiveFromScroll = () => {
  let current = navLinks[0]?.href ?? "#visao-geral";
  for (const link of navLinks) {
    const sectionEl = document.querySelector(link.href);
    if (!sectionEl) continue;
    const top = sectionEl.getBoundingClientRect().top;
    if (top - topOffsetPx <= 0) {
      current = link.href;
    } else {
      break;
    }
  }
  if (current !== activeNav.value) {
    setActiveNav(current);
  }
};

const onScroll = () => {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = null;
    updateActiveFromScroll();
  });
};

const handleNavClick = (event, href) => {
  event.preventDefault();
  setActiveNav(href);
  history.replaceState(null, "", href);
  const el = document.querySelector(href);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const syncActiveNavFromHash = () => {
  const hash = window.location.hash;
  if (!hash) return;
  if (navLinks.some((l) => l.href === hash)) {
    setActiveNav(hash);
  }
};

onMounted(() => {
  syncActiveNavFromHash();
  window.addEventListener("hashchange", syncActiveNavFromHash, {
    passive: true,
  });
  window.addEventListener("scroll", onScroll, { passive: true });
  updateActiveFromScroll();
});

onUnmounted(() => {
  window.removeEventListener("hashchange", syncActiveNavFromHash);
  window.removeEventListener("scroll", onScroll);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
});

function openDemoForSegmento() {
  window.dispatchEvent(
    new CustomEvent("open-demo-modal", { detail: { segmento: "Restaurante / Food service" } }),
  );
}

// Conteúdo abaixo é baseado no ebook oficial "Kali Gestão Food" (docs/books/KALI FOOD - EBOOK.pdf).
const segmentos = [
  { icon: BuildingStorefrontIcon, label: "Restaurantes" },
  { icon: BoltIcon, label: "Lanchonetes e hamburguerias" },
  { icon: FireIcon, label: "Pizzarias" },
  { icon: CakeIcon, label: "Padarias e confeitarias" },
  { icon: BeakerIcon, label: "Bares e botecos" },
  { icon: GiftTopIcon, label: "Açaiterias e sorveterias" },
  { icon: TruckIcon, label: "Food trucks" },
  { icon: HomeModernIcon, label: "Delivery e dark kitchens" },
  { icon: ScaleIcon, label: "Buffets e self-service" },
  { icon: ClockIcon, label: "Cafeterias" },
  { icon: GiftIcon, label: "Docerias" },
  { icon: ShoppingBagIcon, label: "Marmitarias" },
];

const destaques = [
  {
    eyebrow: "Comandas",
    title: "Controle de comandas sem complicação",
    text: "Individuais ou coletivas, por cliente ou por mesa. Divida os valores por produto, pessoa ou forma de pagamento.",
  },
  {
    eyebrow: "Cozinha",
    title: "Painel da cozinha e do bar em tempo real",
    text: "Os pedidos vão direto para o painel certo, organizando filas e facilitando o gerenciamento das entregas.",
  },
  {
    eyebrow: "Delivery",
    title: "Delivery próprio com painel do entregador",
    text: "Loja virtual para pedidos, controle de taxas e um painel simples pro entregador acompanhar tudo em tempo real.",
  },
  {
    eyebrow: "PDV",
    title: "Frente de caixa rápida e personalizada",
    text: "Venda com agilidade e emita NFC-e direto pelo caixa, enviando por e-mail ou WhatsApp sem complicação.",
  },
];

const documentosFiscais = ["NF-e", "NFC-e"];

const controleTributario = [
  { label: "Sped Fiscal", shade: "bg-kali-blue-700" },
  { label: "Bloco H", shade: "bg-kali-blue-600" },
  { label: "Bloco K", shade: "bg-kali-blue-500" },
  { label: "EFD Contribuições", shade: "bg-kali-blue-400" },
];

const modulos = [
  {
    icon: ClipboardDocumentListIcon,
    title: "Controle de comandas",
    subtitle: "Por mesa, cliente ou número",
    items: [
      "Comandas individuais ou coletivas",
      "Divisão por produto ou pessoa",
      "Divisão por forma de pagamento",
    ],
  },
  {
    icon: FireIcon,
    title: "Painel da cozinha e bar",
    subtitle: "Pedidos organizados em tempo real",
    items: [
      "Fila de pedidos por setor",
      "Organização automática das entregas",
      "Menos erro, mais agilidade",
    ],
  },
  {
    icon: QrCodeIcon,
    title: "Delivery e cardápio digital",
    subtitle: "Do QR Code ao painel do entregador",
    items: [
      "Cardápio digital via QR Code",
      "Loja virtual para pedidos próprios",
      "Frete por KM calculado automaticamente",
    ],
  },
  {
    icon: DevicePhoneMobileIcon,
    title: "Integrações de pedido",
    subtitle: "iFood, ByFood e Anotaaí",
    items: [
      "Sincronização de pedidos em tempo real",
      "Menos erro operacional",
      "Tudo direto no painel da cozinha",
    ],
  },
  {
    icon: CreditCardIcon,
    title: "Frente de caixa e PDV",
    subtitle: "Venda rápida e personalizada",
    items: [
      "Emissão de NFC-e no balcão",
      "PIX dinâmico no PDV",
      "Envio por e-mail ou WhatsApp",
    ],
  },
  {
    icon: BanknotesIcon,
    title: "Gestão financeira",
    subtitle: "Contas em dia, sem sustos",
    items: [
      "Contas a pagar e receber",
      "Conciliação bancária",
      "Boletos registrados integrados ao banco",
    ],
  },
  {
    icon: ArchiveBoxIcon,
    title: "Controle de estoque",
    subtitle: "Da matéria-prima ao produto pronto",
    items: [
      "Apuração automática por notas fiscais",
      "Controle de produção e ficha técnica",
      "Etiquetas personalizadas",
    ],
  },
  {
    icon: PuzzlePieceIcon,
    title: "Soluções integradas",
    subtitle: "Tudo conectado ao sistema",
    items: [
      "Assinador digital",
      "Contrato com faturamento recorrente",
      "CONNECT e SEUPOS",
    ],
  },
];

useSeo({
  title: "Sistema para Restaurantes, Lanchonetes e Delivery | Kali Food",
  description:
    "Controle pedidos, comandas, estoque e entregas com o Kali Food. Sistema completo para alimentação. Teste grátis!",
  image: imageBg,
  schema: {
    "@context": "https://schema.org",
    "@type": ["SoftwareApplication", "LocalBusiness"],
    name: "Kali Food",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://www.kalisistemas.com.br/solucao/kali-food",
    description:
      "Sistema de gestão para restaurantes, lanchonetes e delivery: pedidos, comandas, estoque, entrega e pagamentos integrados.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "BRL",
    },
  },
});
</script>

<template>
  <div class="bg-white">
    <SolutionHero
      title="Kali Food: sistema de gestão para restaurantes, lanchonetes e delivery"
      description="Controle pedidos, comandas, estoque, entregas e pagamentos com um sistema simples, completo e 100% online. Ideal para quem serve no balcão, na mesa ou na casa do cliente."
      primary-label="Solicite uma demonstração gratuita"
      primary-href="/contato?type=kali-food"
      :background-image="imageBg"
      background-class="bg-top bg-cover bg-slate-900"
      overlay-variant="left"
    >
    </SolutionHero>

    <section
      class="sticky top-16 z-40 border-b border-gray-100 shadow-sm backdrop-blur bg-white/95"
    >
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="flex gap-6 items-center h-16">
          <nav
            class="flex overflow-x-auto relative gap-6 items-center h-16"
            aria-label="Navegação da página"
          >
            <a
              v-for="item in navLinks"
              :key="item.href"
              :href="item.href"
              class="menu-item-underline relative inline-flex items-center px-2 h-16 text-sm font-bold text-gray-700 hover:text-[#006086] transition-all duration-300 whitespace-nowrap"
              :class="{ 'text-[#006086] active': activeNav === item.href }"
              @click="(e) => handleNavClick(e, item.href)"
            >
              {{ item.label }}
            </a>
          </nav>
        </div>
      </div>
    </section>

    <!-- Visão geral -->
    <section id="visao-geral" class="overflow-hidden relative py-16 bg-white scroll-mt-24 lg:min-h-[620px]">
      <div class="hidden absolute inset-y-0 right-0 w-1/2 lg:block">
        <img
          :src="visaoGeralImg"
          alt="Kali Food"
          class="object-cover object-center w-full h-full"
        />
      </div>
      <div class="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="lg:max-w-xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Feito para uma gestão eficiente, do balcão ao delivery
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Uma solução de gestão completa, fácil de usar e simples para
            emitir documentos fiscais e controlar a rotina do seu restaurante
            ou lanchonete. O Kali Food automatiza estoque, comandas,
            simplifica a fila de pedidos na cozinha e elimina desperdícios.
            Por ser na nuvem, você acompanha tudo o que acontece, mesmo à
            distância.
          </p>
          <div class="flex gap-8 items-center mt-10 text-[#006086]">
            <DevicePhoneMobileIcon class="w-10 h-10" />
            <ComputerDesktopIcon class="w-10 h-10" />
            <DeviceTabletIcon class="w-10 h-10" />
          </div>
        </div>
      </div>
    </section>

    <!-- Segmentos -->
    <section id="segmentos" class="py-16 bg-gray-100 scroll-mt-24">
      <div class="grid gap-12 items-center px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:grid-cols-[1.3fr_1fr]">
        <div class="grid grid-cols-3 gap-x-6 gap-y-10 sm:grid-cols-4">
          <div
            v-for="s in segmentos"
            :key="s.label"
            v-reveal
            class="flex flex-col items-center text-center group"
          >
            <component
              :is="s.icon"
              class="w-11 h-11 text-kali-blue-600 transition-transform duration-200 ease-out group-hover:-translate-y-1"
            />
            <p
              class="mt-3 text-xs font-semibold text-gray-800 transition-colors duration-200 sm:text-sm group-hover:text-kali-blue-600"
            >
              {{ s.label }}
            </p>
          </div>
        </div>
        <div v-reveal>
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Soluções que atendem à realidade de cada
            <span class="text-kali-blue-600">operação</span>
            de alimentação
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            O Kali Food atende diferentes formatos de negócio de alimentação,
            do balcão ao delivery, se adaptando à realidade operacional e às
            necessidades de cada cozinha.
          </p>
          <button
            type="button"
            @click="openDemoForSegmento"
            class="inline-flex justify-center items-center px-6 py-3 mt-6 text-base font-semibold text-white rounded-full border border-transparent shadow-sm bg-kali-blue-500 hover:bg-kali-blue-600"
          >
            Atendemos diversos formatos de food service
          </button>
        </div>
      </div>
    </section>

    <!-- Funcionalidades em destaque -->
    <section id="funcionalidades" class="py-20 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2 class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4">
            Feito para quem vive o ritmo da cozinha
          </h2>
          <p class="text-lg leading-8 text-gray-600">
            Da comanda ao delivery, cada funcionalidade foi pensada para
            tirar trabalho manual do seu dia a dia.
          </p>
        </div>
        <div class="grid gap-8 mt-14 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="d in destaques" :key="d.title" v-reveal>
            <div class="flex justify-center items-center w-full h-44 rounded-2xl bg-gradient-to-br from-kali-blue-100 to-kali-blue-200">
              <BuildingStorefrontIcon class="w-12 h-12 text-kali-blue-500" />
            </div>
            <p class="mt-4 text-xs font-bold tracking-wide text-[#006086] uppercase">
              {{ d.eyebrow }}:
            </p>
            <h3 class="mt-1 text-lg font-bold text-gray-900">{{ d.title }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ d.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Reforma tributária -->
    <section class="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div
        v-reveal
        class="overflow-hidden relative bg-kali-blue-600 rounded-[3rem] shadow-xl"
      >
        <div class="grid gap-10 items-center px-8 py-12 lg:grid-cols-2 lg:px-16 lg:py-16">
          <div>
            <h2 class="text-2xl font-black tracking-tight text-white sm:text-3xl lg:text-4xl">
              Reforma tributária: é hora de se preparar!
            </h2>
            <p class="mt-4 text-base leading-7 text-kali-blue-100 sm:text-lg">
              O Kali Food acompanha as mudanças da reforma tributária em
              tempo real, mantendo sua empresa em conformidade sem parar de
              vender.
            </p>
            <a
              href="/reforma-tributaria"
              class="inline-flex justify-center items-center px-8 py-3 mt-8 text-sm font-bold tracking-wide text-kali-blue-700 uppercase bg-white rounded-full shadow-sm transition hover:bg-kali-blue-50"
            >
              Saiba mais
            </a>
          </div>
          <div class="hidden relative lg:block lg:h-80">
            <img
              :src="gestaoTributaria"
              alt="Especialistas em conformidade fiscal"
              class="object-cover absolute inset-0 w-4/5 h-full rounded-3xl shadow-lg"
            />
            <div
              class="flex absolute -top-6 left-4 justify-center items-center w-16 h-16 rounded-2xl shadow-lg bg-kali-gold"
            >
              <DocumentCheckIcon class="w-8 h-8 text-white" />
            </div>
            <div
              class="flex absolute -bottom-6 right-4 justify-center items-center w-20 h-20 bg-cyan-400 rounded-2xl shadow-lg"
            >
              <ScaleIconOutline class="w-9 h-9 text-white" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Fiscal -->
    <section id="fiscal" class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="grid gap-12 lg:grid-cols-2">
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Documentos fiscais
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Emita documentos fiscais de forma descomplicada, de forma
              automática e vinculada ao site da Receita Federal.
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="d in documentosFiscais"
                :key="d"
                class="flex justify-center items-center py-4 text-lg font-bold rounded-lg bg-kali-blue-100 text-kali-blue-700"
              >
                {{ d }}
              </div>
            </div>
            <ul class="mt-6 space-y-2 text-sm text-gray-700">
              <li>
                <span class="font-semibold text-gray-900">Entrada de Notas —</span>
                de forma automática, vinculado ao site da Receita Federal.
              </li>
              <li>
                <span class="font-semibold text-gray-900">Integração —</span>
                com a maioria das prefeituras do Brasil, atendendo a legislação
                de todos os estados.
              </li>
            </ul>
          </div>
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Controle fiscal e tributário
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Desenvolvido para MEI, ME, Simples, Lucro Real e Lucro
              Presumido, com emissão dos principais documentos fiscais.
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="c in controleTributario"
                :key="c.label"
                class="flex items-end p-4 h-24 font-bold text-white rounded-lg"
                :class="c.shade"
              >
                {{ c.label }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Módulos -->
    <section id="modulos" class="py-16 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <h2
          class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4"
        >
          Módulos do Kali Food
        </h2>
        <p class="mt-4 mb-12 max-w-2xl text-lg leading-8 text-gray-600">
          Conecte o Kali Food a módulos adicionais e leve a gestão do seu
          negócio a outro nível.
        </p>
        <div class="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="m in modulos" :key="m.title" v-reveal class="text-center">
            <component :is="m.icon" class="mx-auto w-10 h-10 text-[#006086]" />
            <h3 class="mt-4 text-lg font-bold text-gray-900">{{ m.title }}</h3>
            <p class="mt-1 text-sm font-semibold text-gray-700">{{ m.subtitle }}</p>
            <ul class="mt-4 space-y-2 text-sm text-gray-600">
              <li v-for="it in m.items" :key="it">{{ it }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Depoimentos -->
    <section id="depoimentos" class="scroll-mt-24">
      <Clients />
    </section>

    <section id="duvidas" class="scroll-mt-24">
      <FaqPanel
        :items="[
          {
            question: 'Faz controle de comandas e mesas?',
            answer:
              'Sim. Controle de mesas, comandas e pedidos com integração ao caixa.',
          },
          {
            question: 'Integra com delivery?',
            answer:
              'Sim. Registro de entregas e integração com meios de pagamento.',
          },
          {
            question: 'Controle de estoque por receita?',
            answer: 'Sim. Baixa de ingredientes por preparo e controle de custo.',
          },
          {
            question: 'Emite NFC-e no balcão?',
            answer: 'Sim. Emissão com QR Code e envio por e-mail/WhatsApp.',
          },
        ]"
        description="Dúvidas sobre Kali Food? Veja as respostas e fale com nosso time."
        cta-label="Falar com Consultor"
        :cta-opens-modal="true"
      />
    </section>

    <section id="fale-conosco" class="scroll-mt-16">
      <CTA />
    </section>
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
